// The client side of /api/sequences (BUILD-BRIEF-R2 §10) — owned by the `journeys` agent.
//
// Every shape here is the server's shape. `CampaignsSummary` is the rail's one round trip and
// is declared here because it is a RESPONSE, not a row — the same place `SignalsSummary` lives.
import { qs, request } from './client.js';
import type { Enrolment, ListResponse, Sequence, Step } from '../../shared/types.js';

export interface CampaignsSummary {
  personas: { total: number; segment: number; person_held: number };
  sequences: { total: number; running: number; draft: number; paused: number; ended: number };
  enrolments: {
    total: number; active: number; replied: number; suppressed: number;
    completed: number; stopped: number; paused: number;
  };
  /** Unheld `draft` proposals filed by a journey principal — what is waiting in Review. */
  drafts_waiting: number;
  drafts_by_step: Array<{ sequence_ref: string; step_n: number; count: number }>;
  approved_7d: number;
  sent_7d: number;
  /** `rate` is null below `min_n`; the page then says "too few to say", never a percentage. */
  reply_12m: { n: number; replies: number; rate: number | null; min_n: number };
  suppressed_reasons: Array<{ reason: string; count: number }>;
  hourly: { cap: number; used: number; remaining: number };
}

export function listSequences(params: { status?: string; q?: string } = {}): Promise<ListResponse<Sequence>> {
  return request(`/sequences${qs(params)}`);
}

export function campaignsSummary(): Promise<CampaignsSummary> {
  return request('/sequences/summary');
}

export function getSequence(id: number): Promise<Sequence> {
  return request(`/sequences/${id}`);
}

export function createSequence(input: {
  name: string; steps: Step[]; daily_cap?: number; segment_persona_id?: number | null;
}): Promise<Sequence> {
  return request('/sequences', { method: 'POST', body: JSON.stringify(input) });
}

export function updateSequence(
  id: number,
  patch: Partial<Pick<Sequence, 'name' | 'steps' | 'daily_cap' | 'status' | 'segment_persona_id'>>,
): Promise<Sequence> {
  return request(`/sequences/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

/** Ask before saving: a mistyped branch is a sentence under the form, not a toast after. */
export function validateSteps(steps: Step[]): Promise<{ ok: boolean; steps?: Step[]; error?: string }> {
  return request('/sequences/validate-steps', { method: 'POST', body: JSON.stringify({ steps }) });
}

export type SequenceVerb = 'start' | 'pause' | 'resume' | 'end';

export function setSequenceStatus(id: number, verb: SequenceVerb): Promise<Sequence> {
  return request(`/sequences/${id}/${verb}`, { method: 'POST' });
}

export function listEnrolments(sequenceId: number, params: { status?: string } = {}): Promise<ListResponse<Enrolment>> {
  return request(`/sequences/${sequenceId}/enrolments${qs(params)}`);
}

export function enrol(sequenceId: number, personIds: number[]): Promise<ListResponse<Enrolment>> {
  return request(`/sequences/${sequenceId}/enrol`, { method: 'POST', body: JSON.stringify({ person_ids: personIds }) });
}

export function stopEnrolment(id: number, reason?: string): Promise<Enrolment> {
  return request(`/sequences/enrolments/${id}/stop`, { method: 'POST', body: JSON.stringify({ reason: reason ?? null }) });
}

export function resumeEnrolment(id: number): Promise<Enrolment> {
  return request(`/sequences/enrolments/${id}/resume`, { method: 'POST' });
}

/** Run `journeys.tick` now — the same path the scheduler takes, overlap guard included. */
export function runTick(): Promise<{ name: string; status: string; ms: number; error?: string; detail?: unknown }> {
  return request('/sequences/tick', { method: 'POST' });
}
